import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { applicationApi } from '@/api/endpoints'
import { cn } from '@/lib/utils'
import type { ApplicationDetail, QuestionLevel, ScreenBlockerNeeds, ScreenMissing } from '@/types/api'

/** The level's heading, as the person reads it. */
export const LEVEL_LABEL: Record<QuestionLevel, string> = {
  profile: 'About you',
  experience: 'Your experience',
  education: 'Your education',
  skills: 'Your skills',
  application: 'This application',
  account: 'Account',
  document: 'Documents',
  consent: 'Declarations',
  declaration: 'Declarations',
}
const LEVEL_ORDER: QuestionLevel[] = ['profile', 'experience', 'education', 'skills', 'application', 'account', 'document', 'consent', 'declaration']

/** What the person has to do, as a short label and a tone. */
export const NEEDS_META: Record<ScreenBlockerNeeds, { label: string; tone: string }> = {
  solve_live: { label: 'Solve live', tone: 'bg-danger-soft text-danger' },
  answers: { label: 'Answer', tone: 'bg-warning-soft text-warning' },
  upload: { label: 'Upload', tone: 'bg-warning-soft text-warning' },
  retry: { label: 'Retry', tone: 'bg-surface-muted text-ink-soft' },
  wait: { label: 'Waiting', tone: 'bg-info/12 text-info' },
  code_fix: { label: 'Needs a fix', tone: 'bg-surface-muted text-ink-muted' },
  nothing: { label: 'Nothing to do', tone: 'bg-surface-muted text-ink-muted' },
}

/** The missing fields by level, in reading order, levels with nothing left out. */
export function missingByLevel(missing: ScreenMissing[]): Array<[QuestionLevel, ScreenMissing[]]> {
  const groups = new Map<QuestionLevel, ScreenMissing[]>()
  for (const item of missing) {
    const level = LEVEL_ORDER.includes(item.level) ? item.level : 'application'
    const list = groups.get(level) ?? []
    list.push(item)
    groups.set(level, list)
  }
  return LEVEL_ORDER.filter((level) => groups.has(level)).map((level) => [level, groups.get(level)!])
}

/**
 * "Why it stopped": the screen the last run stopped on — the step, what
 * blocked it there (a CAPTCHA refused three times, three answers missing, a
 * photo the profile does not have…), the site's own words, what was tried,
 * the picture of the page, and the missing fields grouped by level. Shown
 * only while the application is waiting on someone.
 */
export function ScreenStateCard({ application, className }: { application: ApplicationDetail; className?: string }) {
  const screen = application.screen
  if (!screen || application.status === 'submitted') return null
  const needs = NEEDS_META[screen.blocker.needs] ?? NEEDS_META.retry
  const groups = missingByLevel(screen.missing)
  const required = screen.documents.filter((d) => d.required)
  return (
    <section className={cn('card px-4 py-4 sm:px-5', className)} aria-label="Why it stopped">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="m-0 text-[14.5px] font-semibold text-ink">Why it stopped</h2>
        <span className={cn('rounded-pill px-2 py-0.5 text-[11.5px] font-medium', needs.tone)}>{needs.label}</span>
        {screen.step && <span className="text-[12.5px] text-ink-muted">on “{screen.step}”</span>}
        {screen.live?.parked && <span className="rounded-pill bg-brand-soft px-2 py-0.5 text-[11.5px] font-medium text-brand">Page open and waiting</span>}
      </div>
      <p className="mt-2 text-[13.5px] leading-relaxed text-ink">{screen.blocker.message}</p>
      {screen.counts.fields > 0 && (
        <p className="mt-1 text-[12.5px] text-ink-muted">
          Filled {screen.counts.filled} of {screen.counts.fields} fields
          {screen.counts.missingRequired ? ` · ${screen.counts.missingRequired} required still missing` : ''}
          {screen.counts.optionalEmpty ? ` · ${screen.counts.optionalEmpty} optional left empty` : ''}
        </p>
      )}
      {screen.blocker.siteWords && (
        <p className="mt-2 text-[12.5px] text-ink-soft">
          <span className="font-medium text-ink">The site said:</span> “{screen.blocker.siteWords}”
        </p>
      )}
      {screen.blocker.element?.label && (
        <p className="mt-1 text-[12.5px] text-ink-soft">
          <span className="font-medium text-ink">Field:</span> {screen.blocker.element.label}
        </p>
      )}
      {Boolean(screen.blocker.tried?.length) && (
        <div className="mt-2 text-[12.5px] text-ink-soft">
          <span className="font-medium text-ink">What we tried:</span>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {screen.blocker.tried!.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {required.length > 0 && (
        <p className="mt-2 text-[12.5px] text-ink-soft">
          <span className="font-medium text-ink">Documents the form wants:</span> {required.map((d) => d.label).join(', ')}
        </p>
      )}
      {groups.length > 0 && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {groups.map(([level, items]) => (
            <div key={level} className="rounded-md border border-line px-3 py-2">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted">{LEVEL_LABEL[level]}</div>
              <ul className="mt-1 space-y-0.5 text-[13px] text-ink">
                {items.map((item) => (
                  <li key={item.key} className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 break-words">{item.label}</span>
                    {item.required && <span className="shrink-0 text-[11px] font-medium text-warning">required</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {screen.screenshot && <ScreenPicture applicationId={application.id} name={screen.screenshot} />}
      {screen.url && (
        <p className="mt-2 truncate text-[12px] text-ink-faint">
          <a href={screen.url} target="_blank" rel="noreferrer" className="hover:underline">{screen.url}</a>
        </p>
      )}
    </section>
  )
}

function ScreenPicture({ applicationId, name }: { applicationId: string; name: string }) {
  const query = useQuery({
    queryKey: ['screen-state-shot', applicationId, name],
    queryFn: () => applicationApi.screenshot(applicationId, name),
    staleTime: Infinity,
    retry: false,
  })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!query.data) return
    const objectUrl = URL.createObjectURL(query.data)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [query.data])
  if (!url) return null
  return (
    <a href={url} target="_blank" rel="noreferrer" title="Open full size" className="mt-3 block overflow-hidden rounded-md border border-line">
      <img src={url} alt="The screen as the run left it" className="max-h-72 w-full object-cover object-top" />
    </a>
  )
}
